import React, {
  ComponentProps,
  CSSProperties,
  Dispatch,
  ElementType,
  forwardRef,
  Ref,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ColumnInstance,
  HeaderGroup,
  Row,
  TableBodyProps,
  TablePropGetter,
  TableProps,
} from 'react-table';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import styled from '@mint-lib/styled';

import { ROW_HEIGHT } from '../../../../constants/campaignBuilder.js';
import { VERSION_ROW_TYPES } from '../../../../constants/versionsColumnsObject.js';
import { useTableScale } from '../../../../hooks/useTableScale.js';
import { VersionsTableRow } from '../../../../types/versions.js';
import VersionsTableHead from './VersionsTableHead/index.js';
import VersionTableRow from './VersionsTableRow/VersionsTableRow.jsx';

const MARGIN_TOP = 32;
const VERSIONS_PAGE_HEADER_CONTENT = 252;
const BORDER_WIDTH = 1;

type Props = {
  getTableProps: () => TableProps;
  getTableBodyProps: (propGetter?: TablePropGetter<object>) => TableBodyProps;
  headerGroups: HeaderGroup<object>[];
  rows: VersionsTableRow[] & Row<{}>[];
  prepareRow: (row: Row<{}>) => void;
  columns: ColumnInstance<{}>[];
  expandedType: VERSION_ROW_TYPES;
  setExpandedType?: Dispatch<SetStateAction<VERSION_ROW_TYPES>>;
};

const VersionsTable = ({
  getTableProps,
  getTableBodyProps,
  headerGroups,
  rows,
  prepareRow,
  columns,
  expandedType,
  setExpandedType,
}: Props) => {
  const scale = useTableScale();

  const [isScrollLeftShown, setShowLeftScroll] = useState<boolean>(false);
  const [isScrollRightShown, setShowRightScroll] = useState<boolean>(true);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const columnHeadRef = useRef<HTMLTableRowElement>(null);
  const listRef = useRef<{
    resetAfterIndex: (index: number, isReset: boolean) => void;
  }>(null);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      const { scrollLeft, scrollWidth, clientWidth } =
        event.target as HTMLElement;

      if (!!scrollLeft && !isScrollLeftShown) {
        setShowLeftScroll(true);
      }
      if (!scrollLeft && isScrollLeftShown) {
        setShowLeftScroll(false);
      }
      if (!!(scrollWidth - clientWidth - scrollLeft) && !isScrollRightShown) {
        setShowRightScroll(true);
      }

      if (!(scrollWidth - clientWidth - scrollLeft) && isScrollRightShown) {
        setShowRightScroll(false);
      }
    };
    wrapperRef.current?.addEventListener('scroll', handleScroll, {
      passive: true,
    });
    if (wrapperRef.current) {
      handleScroll({
        target: wrapperRef.current,
      } as any);
    }

    return () => {
      wrapperRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [isScrollLeftShown, isScrollRightShown]);

  useEffect(() => {
    listRef?.current?.resetAfterIndex(0, false);
  }, [rows]);

  useEffect(() => {
    listRef?.current?.resetAfterIndex(0, true);
  }, [scale]);

  const columnsWidth = useMemo(
    () =>
      columns.reduce((acc, item) => {
        const widthItem = typeof item.width === 'number' ? item.width : 0;

        return acc + widthItem;
      }, 0),
    [columns],
  );

  const bodyProps = useMemo(() => getTableBodyProps(), []);
  const getItemSize = () => ROW_HEIGHT * scale;

  const RenderRow = useCallback(
    ({ index, style }: { index: number; style: CSSProperties }) => {
      const row = rows[index];
      prepareRow(row);

      return <VersionTableRow style={style} row={row} />;
    },
    [prepareRow, rows],
  );

  //  Fixing the issue requires upgrade of react-table and react-window versions
  const innerElementType = /*NOSONAR */ forwardRef(
    (
      {
        style,
        children,
      }: { style: { [key: string]: string }; children: ElementType[] },
      ref: Ref<HTMLTableRowElement>,
    ) => (
      <Table $width={columnsWidth} {...getTableProps()} style={style}>
        <VersionsTableHead
          headerGroups={headerGroups}
          ref={ref}
          expandedType={expandedType}
          setExpandedType={setExpandedType}
        />
        <VersionsTableBody {...bodyProps}>{children}</VersionsTableBody>
      </Table>
    ),
  );

  return (
    <AutoSizerWrapper>
      <AutoSizer>
        {({ height, width }) => (
          <StyledVariableSizeList
            height={height - MARGIN_TOP}
            itemCount={rows.length}
            itemSize={getItemSize}
            width={width - 2 * BORDER_WIDTH}
            innerElementType={innerElementType}
            outerRef={wrapperRef}
            innerRef={columnHeadRef}
            ref={listRef as React.Ref<VariableSizeList<unknown>>}
          >
            {RenderRow}
          </StyledVariableSizeList>
        )}
      </AutoSizer>
    </AutoSizerWrapper>
  );
};

const AutoSizerWrapper = styled('div')`
  height: calc(100vh - ${VERSIONS_PAGE_HEADER_CONTENT}px);
  margin: ${({ theme }) => `${MARGIN_TOP} ${theme.spacing(1.75)} 0 0`};
  border-color: ${({ theme }) => theme.palette.border.secondary};
  border-style: solid;
  border-width: 0 1px 1px 1px;
  border-radius: 6px 0 6px 6px;
`;

const VersionsTableBody = styled('div')`
  position: relative;
`;

const StyledVariableSizeList = styled(VariableSizeList)`
  border-color: ${({ theme }) => theme.palette.border.secondary};
  border-style: solid;
  border-width: 1px 0 0 0;
  border-top-left-radius: 6px;
`;

const Table = styled(
  ({
    className = 'table',
    $width,
    ...props
  }: ComponentProps<'div'> & { $width: number }) => (
    <div className={className} {...props} />
  ),
)`
  min-width: 100% !important;
  width: ${({ $width }) => $width}px !important;
  min-height: 100% !important;
`;

export default VersionsTable;
